/**
 * data-beacon click tracking.
 *
 * The renderer emits `data-beacon` attributes on clickable result markup (web
 * results, related searches, yahoo ads/cta). When the user clicks such a link
 * we fire its beacon URL. Ported from @serp/client/src/main.ts so the unified
 * @rampjs/client bundle preserves these click pingbacks on renderer SSR pages
 * (ramp.js never had this — it only ran on publisher pages without the
 * renderer's data-beacon markup).
 *
 * Transport note: this intentionally fires a raw `fetch(GET, no-cors,
 * keepalive)` rather than the `sendPingback` (sendBeacon/POST) transport the
 * ad-loaded path uses. These beacon endpoints are the renderer's click
 * trackers and have always been hit with GET by @serp/client; preserving the
 * method avoids changing what the endpoints receive.
 */

import {
  appendPartnerCookies,
  stripLandingAttribution,
} from "@ascent/papi-core/client";

const wiredBeacons = new WeakMap<EventTarget, Set<string>>();

function wire(
  target: EventTarget,
  type: string,
  beaconUrl: string,
  listener: () => void,
): void {
  const key = `${type}\n${beaconUrl}`;
  let keys = wiredBeacons.get(target);
  if (!keys) {
    keys = new Set();
    wiredBeacons.set(target, keys);
  }
  if (keys.has(key)) return;
  keys.add(key);
  target.addEventListener(type, listener);
}

/**
 * Wire up click tracking for every `data-beacon` the renderer emitted. Safe to
 * call again after a later placement: an element already wired for the same
 * beacon is left alone. Covers two markup shapes:
 *   1. `<a data-beacon="...">` — beacon on the link itself.
 *   2. `<div data-beacon="..."> ... <a> ... </a> </div>` — beacon on a parent
 *      element wrapping one or more links (links with their own data-beacon are
 *      left to case 1 so they aren't double-fired).
 */
export function setupClickTracking(): void {
  // Search forms: the form's "click" is its submit (Enter or the button).
  document
    .querySelectorAll<HTMLFormElement>("form[data-beacon]")
    .forEach((form) => {
      const beaconUrl = form.getAttribute("data-beacon");
      if (!beaconUrl) return;
      wire(form, "submit", beaconUrl, () => fireBeacon(beaconUrl));
    });

  // Case 1: links that carry their own data-beacon.
  const beaconLinks =
    document.querySelectorAll<HTMLAnchorElement>("a[data-beacon]");

  beaconLinks.forEach((link) => {
    const beaconUrl = link.getAttribute("data-beacon");
    if (!beaconUrl) return;

    wire(link, "click", beaconUrl, () => {
      fireBeacon(beaconUrl);
      updateRelatedSearchLink(link);
    });
  });

  // Case 2: non-anchor elements with data-beacon that contain links. Forms
  // are handled above, on submit.
  const beaconElements = document.querySelectorAll(
    "[data-beacon]:not(a):not(form)",
  );

  beaconElements.forEach((element) => {
    const beaconUrl = element.getAttribute("data-beacon");
    if (!beaconUrl) return;

    const links = element.querySelectorAll<HTMLAnchorElement>("a");

    links.forEach((link) => {
      // Skip if the link already has its own data-beacon (handled in case 1).
      if (link.hasAttribute("data-beacon")) return;

      wire(link, "click", beaconUrl, () => {
        fireBeacon(beaconUrl);
        updateRelatedSearchLink(link);
      });
    });
  });
}

/**
 * Fire a beacon URL asynchronously. Partner cookies are stamped on first, then
 * the URL is hit with a fire-and-forget GET (see transport note above).
 */
function fireBeacon(beaconUrl: string): void {
  try {
    const url = new URL(beaconUrl);
    appendPartnerCookies(url);
    beaconUrl = url.toString();
  } catch {
    // keep original URL if parsing fails
  }

  fetch(beaconUrl, {
    method: "GET",
    mode: "no-cors",
    keepalive: true,
  }).catch((error) => {
    console.debug("Beacon error:", error);
  });
}

/**
 * For related-search links, rewrite the href to carry the clicked term as `q`
 * so the destination search preserves the current page's other query params,
 * minus this page's landing attribution (aylf_query, serp_source, …).
 * Links the renderer built from papi-core's clickUrl (`data-server-href`)
 * already carry rc / aylf_query / serp_source and are left untouched.
 */
function updateRelatedSearchLink(link: HTMLAnchorElement): void {
  if (
    !link.classList.contains("related-search-link") ||
    link.hasAttribute("data-server-href")
  ) {
    return;
  }

  const searchTerm = link.getAttribute("data-search-term");
  if (searchTerm) {
    const currentUrl = new URL(window.location.href);
    stripLandingAttribution(currentUrl.searchParams);
    currentUrl.searchParams.set("q", searchTerm);
    link.href = currentUrl.toString();
  }
}
